import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, Eye, FloppyDisk, TrendUp, TrendDown, Minus, ArrowClockwise,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Rolling ELO rebaseline admin card.
 *
 * Philosophy: each player has a `baseline_elo` (premier winners ≥ 1100, all
 * challengers = 1000). Match ELO deltas accumulate on top during a tournament.
 * When admin hits "Apply rolling rebaseline", we compute:
 *     new_elo = baseline_elo + Σ (elo_after − elo_before) for matches since
 *                                                          last rebaseline
 * and a new rebaseline timestamp is stored. Snapshot-based rollback is always
 * available via the existing "Rebaseline Premier ELO → Rollback latest" button.
 *
 * Suggested cadence: after each tournament OR every 2 weeks.
 */
export default function RollingRebaselineCard() {
  const [meta, setMeta] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadMeta = async () => {
    try {
      const r = await axios.get(`${API}/admin/elo-baseline`, { withCredentials: true });
      setMeta(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load baseline state");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMeta(); }, []);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.post(`${API}/admin/rebaseline-rolling/preview`, {}, { withCredentials: true });
      setPreview(r.data);
      toast.success(`Preview ready — ${r.data.player_count} players`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      "Apply the rolling rebaseline to every baselined player?\n\n" +
      "Each player gets: elo_rating = baseline_elo + Σ (match ELO deltas since last rebaseline)\n\n" +
      "A snapshot is taken automatically — you can roll back via Rebaseline Premier ELO → Rollback."
    )) return;
    const pw = await promptSuperPassword({
      title: "Apply rolling rebaseline?",
      description: "Each player's ELO is rebuilt from baseline + match deltas. Snapshot saved for rollback.",
      actionLabel: "Apply Rolling Rebaseline",
    });
    if (!pw) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/rebaseline-rolling/apply`, {}, { headers: { "X-Super-Password": pw }, withCredentials: true });
      toast.success(r.data.message);
      setPreview(null);
      await loadMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-6 text-center text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  const tierSummary = meta?.tier_baselines || {};
  const formatAgo = (iso) => {
    if (!iso) return "never";
    try {
      const d = new Date(iso);
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
      return `${Math.floor(mins / 1440)}d ago`;
    } catch { return iso; }
  };

  return (
    <Card className="bg-card border-border" data-testid="rolling-rebaseline-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ArrowClockwise className="w-5 h-5 text-secondary" weight="duotone" />
          Rolling ELO Rebaseline
        </CardTitle>
        <CardDescription className="text-xs mt-1">
          Resets each player's ELO to their baseline + their net tournament delta since the last
          rebaseline. Run after a tournament or every 2 weeks.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Meta summary */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="rounded px-2 py-1.5 bg-background border border-border">
            <p className="text-muted-foreground">Last rebaseline</p>
            <p className="font-semibold flex items-center gap-1">
              <Clock className="w-3 h-3" /> {formatAgo(meta?.last_rebaseline_at)}
            </p>
          </div>
          <div className="rounded px-2 py-1.5 bg-background border border-border">
            <p className="text-muted-foreground">Matches since</p>
            <p className="font-heading font-bold text-lg tabular-nums">
              {meta?.matches_completed_since ?? 0}
            </p>
          </div>
          <div className="rounded px-2 py-1.5 bg-background border border-border">
            <p className="text-muted-foreground">Players baselined</p>
            <p className="font-heading font-bold text-lg tabular-nums">
              {Object.values(tierSummary).reduce((a, b) => a + (b?.count || 0), 0)}
            </p>
          </div>
        </div>

        {/* Tier summary */}
        <div className="space-y-1">
          {Object.entries(tierSummary).map(([tier, s]) => (
            <div key={tier} className="flex items-center gap-2 text-xs">
              <Badge variant="outline" className={`capitalize ${tier === "premier" ? "border-primary/30 text-primary" : "border-border"}`}>
                {tier}
              </Badge>
              <span className="text-muted-foreground">
                {s.count} players · baseline {s.min === s.max ? s.min : `${s.min}–${s.max}`}
              </span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          <Button
            size="sm" variant="outline" onClick={runPreview} disabled={previewing}
            className="border-secondary/40 text-secondary hover:bg-secondary/10"
            data-testid="rolling-preview-btn"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {previewing ? "Computing…" : "Preview"}
          </Button>
          <Button
            size="sm" onClick={runApply} disabled={applying}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="rolling-apply-btn"
          >
            <FloppyDisk className="w-4 h-4 mr-1.5" weight="fill" />
            {applying ? "Applying…" : "Apply rolling rebaseline"}
          </Button>
        </div>

        {/* Preview rows */}
        {preview && (
          <div className="border border-border rounded-lg" data-testid="rolling-preview-results">
            <div className="px-4 py-2 border-b border-border bg-muted/10">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview — {preview.rows?.length || 0} players
              </span>
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {(preview.rows || []).map((r) => {
                const up = r.change > 0;
                const down = r.change < 0;
                const Icon = up ? TrendUp : down ? TrendDown : Minus;
                const colour = up ? "text-primary" : down ? "text-destructive" : "text-muted-foreground";
                return (
                  <div key={r.player_id} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <Badge variant="outline" className={`text-[10px] capitalize w-20 justify-center ${r.tier === "premier" ? "border-primary/30 text-primary" : ""}`}>
                      {r.tier}
                    </Badge>
                    <span className="flex-1 font-medium truncate">{r.name}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right">
                      base {r.baseline_elo}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right">
                      Δ {r.tournament_delta > 0 ? "+" : ""}{r.tournament_delta}
                    </span>
                    <span className="font-heading font-bold tabular-nums w-14 text-right">
                      {r.new_elo}
                    </span>
                    <span className={`inline-flex items-center gap-0.5 text-xs font-bold tabular-nums w-14 justify-end ${colour}`}>
                      <Icon className="w-3 h-3" weight="bold" />
                      {up || down ? (up ? "+" : "") + r.change : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          <strong>Note:</strong> ELO deltas from matches are preserved, not wiped. Placements, match
          points and tier assignments are never touched.
        </p>
      </CardContent>
    </Card>
  );
}
